import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckIcon } from '@repo/icons';
import classNames from 'classnames';
import { isValidElement, useId } from 'react';
const containerStyles = {
    base: 'flex rounded-xl shadow-xs outline-0 ring-1 relative p-3 gap-3',
    interactive: 'cursor-pointer hover:bg-tertiary-medium focus:bg-secondary-strong focus:ring-2 focus:ring-stroke-brand-subtle focus:text-body',
    default: 'bg-primary-soft ring-stroke-base',
    checked: 'bg-brand-softer ring-stroke-brand',
    disabled: 'cursor-not-allowed opacity-60',
};
const iconStyles = {
    base: '',
    default: 'text-body',
    checked: 'text-fg-brand-strong',
};
const labelStyles = {
    base: 'select-none text-sm font-medium leading-5 break-words',
    default: 'text-heading',
    checked: 'text-heading',
};
const descriptionStyles = {
    base: 'select-none text-sm leading-5',
    default: 'text-body',
    checked: 'text-body',
};
const checkboxInputStyles = {
    base: 'size-4 shrink-0 rounded border outline-none appearance-none flex items-center justify-center',
    interactive: 'cursor-pointer',
    default: 'border-stroke-base-strong bg-secondary-strong',
    checked: 'bg-brand border-stroke-brand',
    disabled: 'cursor-not-allowed',
};
const renderIcon = (icon, className) => {
    if (isValidElement(icon))
        return icon;
    const Icon = icon;
    return _jsx(Icon, { className: className, "aria-hidden": true, size: 28 });
};
export default function BorderedCheckbox({ checked = false, children, className, description, disabled = false, icon, label, onChange, checkboxOn, testId, ...inputProps }) {
    const inputId = useId();
    const hasIcon = Boolean(icon);
    const containerClasses = classNames(containerStyles.base, hasIcon ? 'flex-col' : classNames('items-start', checkboxOn === 'left' ? 'flex-row-reverse' : 'flex-row'), !disabled && containerStyles.interactive, checked ? containerStyles.checked : containerStyles.default, disabled && containerStyles.disabled, className);
    const iconRowClasses = classNames('flex items-center justify-between gap-3', checkboxOn === 'left' && 'flex-row-reverse');
    const iconClasses = classNames(iconStyles.base, checked ? iconStyles.checked : iconStyles.default);
    const labelClasses = classNames(labelStyles.base, checked ? labelStyles.checked : labelStyles.default);
    const descriptionClasses = classNames(descriptionStyles.base, checked ? descriptionStyles.checked : descriptionStyles.default);
    const checkboxClasses = classNames(checkboxInputStyles.base, !disabled && checkboxInputStyles.interactive, checked ? checkboxInputStyles.checked : checkboxInputStyles.default, disabled && checkboxInputStyles.disabled);
    const handleChange = () => {
        if (disabled)
            return;
        onChange?.(!checked);
    };
    return (_jsxs("label", { className: containerClasses, "data-testid": testId, tabIndex: disabled ? undefined : 0, htmlFor: inputId, role: "checkbox", "aria-checked": checked, "aria-disabled": disabled, children: [hasIcon && icon && (_jsxs("div", { className: iconRowClasses, children: [renderIcon(icon, iconClasses), _jsxs("div", { children: [_jsx("input", { id: inputId, type: "checkbox", tabIndex: -1, checked: checked, disabled: disabled, onChange: handleChange, className: "sr-only", "data-testid": testId.append('input'), ...inputProps }), _jsx("div", { className: checkboxClasses, "aria-hidden": true, children: checked && _jsx(CheckIcon, { size: 16, className: "text-white" }) })] })] })), _jsx("div", { className: "flex min-w-0 flex-1 flex-col gap-2", children: children ? (children) : (_jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [label && _jsx("span", { className: labelClasses, children: label }), description && _jsx("span", { className: descriptionClasses, children: description })] })) }), !hasIcon && (_jsxs("div", { children: [_jsx("input", { id: inputId, type: "checkbox", tabIndex: -1, checked: checked, disabled: disabled, onChange: handleChange, className: "sr-only", "data-testid": testId.append('input'), ...inputProps }), _jsx("div", { className: checkboxClasses, "aria-hidden": true, children: checked && _jsx(CheckIcon, { size: 16, className: "text-white" }) })] }))] }));
}
